import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSheetDrag } from './studio/useSheetDrag'
import CreatorFrame from '../../../../shared/CreatorFrame'
import { useScrollLock } from '../../../../../_utils/useScrollLock'
import { hapticTick, hapticSuccess } from '../../../../../_utils/haptics'
import { POST_TYPES, slidesFor, photosFor } from '../data/postTypes'
import { savePost, addFavourite, findFavourite, removeFavourite, restoreFavourite, loadFavourites, subscribeFavourites, touchFavourite } from '../data/store'
import { favouriteFrom } from '../data/favourites'
import { buildCaption } from '../data/captions'
import { colourFromImage } from '../render/colourways'
import { frameFor } from '../render/kit'
import { loadImage } from '../render/images'
import { postTheme, thumbnail } from '../render/render'
import PostCanvas from './PostCanvas'
import ShareSheet from './ShareSheet'
import { useHistory } from './studio/useHistory'
import DesignsPanel, { lookOnly } from './studio/DesignsPanel'
import { seasonalOf } from '../kits'
import ColoursPanel from './studio/ColoursPanel'
import TextPanel from './studio/TextPanel'
import PhotosPanel, { PhotoPicker } from './studio/PhotosPanel'
import CaptionPanel from './studio/CaptionPanel'
import BaileyPanel from './studio/BaileyPanel'
import InstagramFrame, { FEED_CHROME } from './studio/InstagramFrame'
import { sparkleFrom, scrollWithin } from './magic'
import {
  ChevronLeft, ChevronRight, CloseIcon, InstagramIcon, UndoIcon, HeartIcon,
  RailBailey, RailDesign, RailColour, RailPhotos, RailWords, RailCaption,
} from './icons'

const Seg = ({ value, options, onChange, label }) => (
  <div className="sx-seg" role="radiogroup" aria-label={label}>
    {options.map(o => (
      <button key={o.id} type="button" role="radio" aria-checked={value === o.id} className={`sx-seg-btn${value === o.id ? ' is-on' : ''}`} onClick={() => { if (value !== o.id) { hapticTick(); onChange(o.id) } }}>
        {o.label}
      </button>
    ))}
  </div>
)

const useBoxSize = (ref) => {
  const [size, setSize] = useState({ w: 0, h: 0 })
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight })
    measure()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return size
}

// Phones get the post on top and the tool below it, never over it
const PHONE = 767
const usePhone = () => {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.innerWidth <= PHONE)
  useEffect(() => {
    const on = () => setPhone(window.innerWidth <= PHONE)
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  return phone
}

// While the studio is open: the page behind can't scroll, pickers sit above
// the studio, and on a phone the studio fits the part of the screen the
// keyboard leaves (so the post shrinks instead of hiding)
const useStudioScreen = () => {
  useScrollLock(true)
  useEffect(() => {
    const root = document.documentElement
    document.body.classList.add('sx-studio-open')
    const vv = window.visualViewport
    const fit = () => {
      if (!vv) return
      root.style.setProperty('--sx-vvh', `${Math.round(vv.height)}px`)
      root.style.setProperty('--sx-vvt', `${Math.round(vv.offsetTop)}px`)
    }
    fit()
    if (vv) { vv.addEventListener('resize', fit); vv.addEventListener('scroll', fit) }
    // The frame around the studio never scrolls (a focused field or a jump to
    // one would otherwise move the whole studio up on a phone)
    const frame = document.querySelector('.sx-studio') && document.querySelector('.sx-studio').parentElement
    const pin = () => { if (frame && frame.scrollTop) frame.scrollTop = 0 }
    if (frame) frame.addEventListener('scroll', pin)
    return () => {
      if (frame) frame.removeEventListener('scroll', pin)
      document.body.classList.remove('sx-studio-open')
      root.style.removeProperty('--sx-vvh')
      root.style.removeProperty('--sx-vvt')
      if (vv) { vv.removeEventListener('resize', fit); vv.removeEventListener('scroll', fit) }
    }
  }, [])
}

// The tool rail, like the gift card, stamp card and email creators: tools on
// the left, the post on the right. On a phone the rail is a bottom bar and a
// tool opens between it and the post.
const TOOLS = [
  { id: 'bailey', label: 'Bailey', title: 'Ask Bailey', Icon: RailBailey },
  { id: 'designs', label: 'Designs', title: 'Designs', Icon: RailDesign },
  { id: 'colours', label: 'Colours', title: 'Colours', Icon: RailColour },
  { id: 'text', label: 'Text', title: 'Text', Icon: RailWords },
  { id: 'photos', label: 'Photos', title: 'Photos', Icon: RailPhotos },
  { id: 'caption', label: 'Caption', title: 'Caption and timing', Icon: RailCaption },
]
// The photo a design's colours can be matched to
// The photo the post shows (a design with no photo has none, even when a
// photo is kept for its other designs)
const leadPhoto = (post) => {
  const keys = photosFor(post).map(s => s.key)
  const key = ['main', 'after'].find(k => keys.includes(k))
  return (key && post.photos && post.photos[key]) || null
}
const typing = (el) => !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))

const PostStudio = ({ post: initial, kit, trade, salonName, town, salon, onClose, startTool, askStart, have }) => {
  const phone = usePhone()
  useStudioScreen()
  const history = useHistory(initial)
  const post = history.value
  // A review post that isn't one of the salon's reviews yet opens on Text,
  // where they pick one
  const pickFirst = initial.type === 'review' && !/^review:/.test(initial.source || '') ? 'text' : null
  const [tool, setTool] = useState(() => (typeof window !== 'undefined' && window.innerWidth <= PHONE ? (startTool || pickFirst || null) : (startTool || pickFirst || 'designs')))
  const [tall, setTall] = useState(false)
  const sheetDrag = useSheetDrag({ tall, setTall, close: () => { setTool(null); setTall(false) } })
  const [slideIndex, setSlideIndex] = useState(0)
  const [activeSlot, setActiveSlot] = useState(null)
  const [picking, setPicking] = useState(null)
  const [sharing, setSharing] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [ig, setIg] = useState(false)
  const [magic, setMagic] = useState(false)
  const [landed, setLanded] = useState(0)
  const [toast, setToast] = useState(null)
  // Bailey's last answer and looks, kept while other tools are open
  const [baileyMemo, setBaileyMemo] = useState(() => (askStart ? {
    answer: { asked: askStart.asked, say: askStart.say || '', changes: askStart.changes, needs: askStart.needs, notes: askStart.notes || [], landed: null },
    lookFor: askStart.req && askStart.req.look && askStart.req.look.known ? { text: askStart.req.lookText, keep: askStart.given || [] } : null,
    history: askStart.history || [],
  } : null))
  const ids = useRef({ id: initial.id || null, createdAt: initial.createdAt || null })
  // Posts Bailey made from a sentence are kept straight away
  const dirty = useRef(!!initial.id || !!initial.fromBailey)
  const saveTimer = useRef(null)
  const toastTimer = useRef(null)
  const stageRef = useRef(null)
  const stage = useBoxSize(stageRef)

  const def = POST_TYPES[post.type]
  const format = post.format || 'post'
  const f = frameFor(format)
  const theme = useMemo(() => postTheme(post, { kit }), [post.family, post.layout, post.colourway, post.photoColour, post.tune, kit]) // eslint-disable-line react-hooks/exhaustive-deps
  const slides = slidesFor(post)
  const slide = Math.min(slideIndex, slides.length - 1)
  const fromReview = post.type === 'review' && /^review:/.test(post.source || '')

  // Every change is a step you can undo, and drafts save themselves
  const update = (patch, key) => {
    dirty.current = true
    history.set(p => ({ ...p, ...(typeof patch === 'function' ? patch(p) : patch) }), { key })
  }
  const setContent = (key, value) => update(p => ({
    content: { ...p.content, [key]: value },
    touched: { ...(p.touched || {}), [key]: true },
    caption: p.caption && p.caption.edited ? p.caption : null,
  }), `content-${key}`)
  // Several words at once, one step to undo (a day and its times)
  const setContents = (patch, key) => update(p => ({
    content: { ...p.content, ...patch },
    touched: { ...(p.touched || {}), ...Object.keys(patch).reduce((a, k) => ({ ...a, [k]: true }), {}) },
    caption: p.caption && p.caption.edited ? p.caption : null,
  }), `content-${key}`)
  const tune = (patch, key) => update((p) => {
    const cur = p.tune || {}
    const next = { ...cur, ...(typeof patch === 'function' ? patch(cur) : patch) }
    Object.keys(next).forEach(k => next[k] === undefined && delete next[k])
    return { tune: Object.keys(next).length ? next : undefined }
  }, key)
  const withIds = ({ fromBailey, ...p }) => ({ ...p, id: ids.current.id || undefined, createdAt: ids.current.createdAt || undefined })

  useEffect(() => {
    if (!dirty.current) return undefined
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      // Lists draw the post afresh until the studio closes with a new thumbnail
      const saved = savePost({ ...withIds(post), thumb: null })
      ids.current = { id: saved.id, createdAt: saved.createdAt }
      setSavedAt(Date.now())
    }, 600)
    return () => clearTimeout(saveTimer.current)
  }, [post]) // eslint-disable-line react-hooks/exhaustive-deps

  // "Match my photo": the lead photo's colour, read once per photo
  const lead = leadPhoto(post)
  const leadSrc = lead && lead.src
  useEffect(() => {
    if (!leadSrc) return undefined
    let alive = true
    loadImage(leadSrc, { small: true }).then((img) => {
      const colour = colourFromImage(img)
      if (alive && colour) history.set(p => (p.photoColour === colour ? p : { ...p, photoColour: colour }), { record: false })
    })
    return () => { alive = false }
  }, [leadSrc]) // eslint-disable-line react-hooks/exhaustive-deps

  // Undo and redo from the keyboard, except while typing (the field's own undo)
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || typing(e.target)) return
      const k = e.key.toLowerCase()
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); history.undo() }
      if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); history.redo() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [history.undo, history.redo]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { clearTimeout(toastTimer.current) }, [])
  const say = (text, { undo = false, action = null } = {}) => {
    clearTimeout(toastTimer.current)
    setToast({ text, undo, action, at: Date.now() })
    toastTimer.current = setTimeout(() => setToast(null), 4500)
  }

  // The caption is written the first time it's needed, and again when the
  // post's words change (unless the salon has edited it themselves)
  const caption = post.caption || buildCaption(post, { voice: 'warm', salon: salonName, town })

  const close = async () => {
    clearTimeout(saveTimer.current)
    if (!dirty.current) { onClose(null); return }
    let thumb = null
    try { thumb = await thumbnail(post, theme) } catch (e) { thumb = null }
    const saved = savePost({ ...withIds(post), caption: post.caption || caption, thumb })
    onClose(saved)
  }

  // A design from the Designs panel, the post already in it; another kind of
  // post can be undone
  const chooseDesign = (next) => {
    const kind = next.type !== post.type
    update(() => next, kind ? 'type' : 'design')
    setLanded(n => n + 1)
    if (!kind) return
    setSlideIndex(0)
    setActiveSlot(null)
    const season = seasonalOf(next.layout)
    const what = season ? season.season.name : POST_TYPES[next.type].label.toLowerCase()
    say(`Now ${/^[aeiou]/i.test(what) ? 'an' : 'a'} ${what} post.`, { undo: true })
  }
  const pickColourway = (colourway) => {
    update(p => ({ colourway, tune: lookOnly(p.tune, false) }), 'colourway')
    setLanded(n => n + 1)
  }
  const applyRemix = (next, note, el) => {
    update(() => next, 'remix')
    setLanded(n => n + 1)
    hapticSuccess()
    sparkleFrom(el)
    say(`Changed to ${note}.`, { undo: true })
  }
  // What was asked for in words, done: one step, so one undo brings it back
  const applyAsk = (next, res, el) => {
    dirty.current = true
    history.set(next, { key: 'bailey-ask' })
    if (next.format !== post.format || next.type !== post.type) { setSlideIndex(0); setActiveSlot(null) }
    setLanded(n => n + 1)
    hapticSuccess()
    sparkleFrom(el)
  }
  const captionFor = (p, voice) => buildCaption(p, { voice, salon: salonName, town })
  // One of the salon's reviews, word for word: their words, name, stars, service
  const pickReview = (r, el) => {
    const name = r.firstName || 'A client'
    update(p => ({
      content: { ...p.content, quote: r.text, name, service: r.service || '', rating: Math.max(1, Math.min(5, Number(r.rating) || 5)) },
      touched: { ...(p.touched || {}), quote: true, name: true, service: true, rating: true },
      source: `review:${r.id}`,
      caption: p.caption && p.caption.edited ? p.caption : null,
    }), 'review-pick')
    setLanded(n => n + 1)
    hapticSuccess()
    sparkleFrom(el)
    say(`${name}’s review, word for word.`, { undo: true })
  }
  // Typing one in (from Google or a message): the fields open up, empty
  const typeOwnReview = () => {
    update(p => ({ content: { ...p.content, quote: '', name: '', service: '', rating: 5 }, source: null, caption: p.caption && p.caption.edited ? p.caption : null }), 'review-own')
    setActiveSlot('quote')
    setTimeout(() => {
      const el = document.getElementById('sx-f-quote')
      if (el) { scrollWithin(el); if (!phone) el.focus({ preventScroll: true }) }
    }, 80)
  }

  // Favourite: this design, kept for next time (the Social home starts new
  // posts from it). Filled while this exact look is a favourite.
  const [favs, setFavs] = useState(loadFavourites)
  useEffect(() => subscribeFavourites(() => setFavs(loadFavourites())), [])
  const faved = useMemo(() => { const f = favouriteFrom(post); return findFavourite(f) }, [post, favs]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (initial.favouriteId) touchFavourite(initial.favouriteId) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const toggleFavourite = (el) => {
    hapticTick()
    if (faved) {
      const index = loadFavourites().findIndex(f => f.id === faved.id)
      removeFavourite(faved.id)
      say('Taken out of your favourites.', { action: { label: 'Undo', run: () => restoreFavourite(faved, index) } })
      return
    }
    addFavourite(favouriteFrom(post))
    hapticSuccess()
    sparkleFrom(el, 8)
    say('Saved to your favourites. Start the next one from Social in a tap.')
  }

  const onTapSlot = (slot) => {
    if (/^photo:/.test(slot)) {
      setTool('photos')
      setActiveSlot(slot)
      setTimeout(() => {
        const el = document.getElementById(`sx-photo-${slot.slice(6)}`)
        if (el) scrollWithin(el)
      }, 80)
      return
    }
    if (slot === 'brand') { setTool('text'); setActiveSlot(null); return }
    setTool('text')
    setActiveSlot(slot)
    setTimeout(() => {
      const el = document.getElementById(`sx-f-${slot === 'more' ? 'more' : slot}`)
      if (!el) return
      scrollWithin(el)
      const input = el.matches('input,textarea') ? el : el.querySelector('input,textarea')
      if (input && !phone) input.focus({ preventScroll: true })
    }, 80)
  }
  const movePhoto = (key, patch, historyKey) => update(p => ({ photos: { ...p.photos, [key]: { ...(p.photos[key] || {}), ...patch } } }), historyKey || `photo-${key}`)

  // As big as fits (with Instagram's rows around a feed post when shown),
  // never wider than 520px
  const chrome = ig && format === 'post' ? FEED_CHROME.top + FEED_CHROME.bottom : 0
  const maxW = Math.max(phone ? 64 : 120, Math.min(phone ? 460 : 520, stage.w - (phone ? 16 : 32), ((stage.h - (phone ? 8 : 24) - chrome) * f.W) / f.H))
  const canvasW = Number.isFinite(maxW) ? Math.floor(maxW) : 320
  const toolDef = TOOLS.find(t => t.id === tool)

  const canvas = (
    <PostCanvas
      key={landed}
      className="sx-canvas-live"
      post={post}
      theme={theme}
      format={format}
      slideIndex={slide}
      width={canvasW}
      onTapSlot={onTapSlot}
      onMovePhoto={(k, patch) => movePhoto(k, patch, `drag-${k}`)}
      activeSlot={activeSlot}
    />
  )

  return (
    <CreatorFrame
      name={post.name || ''}
      namePlaceholder={def.label}
      onNameChange={(v) => update({ name: v }, 'name')}
      onClose={close}
      canUndo={history.canUndo}
      canRedo={history.canRedo}
      onUndo={() => { hapticTick(); history.undo() }}
      onRedo={() => { hapticTick(); history.redo() }}
      headerExtra={savedAt ? <span className="sx-saved" key={savedAt}>Saved</span> : null}
      primaryAction={{ label: 'Share', onClick: () => { update({ caption: post.caption || caption }, 'share'); setSharing(true) } }}
    >
      <div className={`sx-studio${tool ? ' has-panel' : ''}${phone ? ' is-phone' : ''}${tall ? ' is-tall' : ''}`}>
        <nav className="sx-rail" aria-label="Tools">
          {TOOLS.map(({ id, label, Icon }) => (
            <button key={id} type="button" className={`sx-rail-btn${tool === id ? ' is-on' : ''}${id === 'bailey' ? ' is-bailey' : ''}`} aria-pressed={tool === id} onClick={() => { hapticTick(); setTool(tool === id ? null : id); setTall(false) }}>
              <span className="sx-rail-icon"><Icon /></span>
              <span className="sx-rail-label">{label}</span>
            </button>
          ))}
        </nav>

        {tool && (
          <aside className="sx-panel" aria-label={toolDef.title}>
            <header className="sx-panel-head" {...(phone ? sheetDrag : {})}>
              {phone && <button type="button" className="sx-grabber" onClick={() => setTall(t => !t)} aria-label={tall ? 'Make the panel smaller' : 'Make the panel bigger'}><span /></button>}
              <h2 className="sx-panel-title">{toolDef.title}</h2>
              <button type="button" className="sx-icon-btn sx-panel-close" onClick={() => { setTool(null); setTall(false) }} aria-label="Close panel"><CloseIcon /></button>
            </header>
            <div className="sx-panel-body" key={tool}>
              {tool === 'bailey' && (
                <BaileyPanel
                  post={post}
                  kit={kit}
                  trade={trade || post.trade}
                  format={format}
                  captionFor={captionFor}
                  have={have}
                  memo={baileyMemo}
                  onMemo={setBaileyMemo}
                  onApply={applyRemix}
                  onAsk={applyAsk}
                  onUndo={() => history.undo()}
                  onGo={onTapSlot}
                  onShare={() => setSharing(true)}
                  onThinking={setMagic}
                />
              )}
              {tool === 'designs' && (
                <DesignsPanel post={post} kit={kit} format={format} onPick={chooseDesign} />
              )}
              {tool === 'colours' && (
                <ColoursPanel post={post} theme={theme} kit={kit} leadSrc={leadSrc} onTune={tune} onColourway={pickColourway} />
              )}
              {tool === 'text' && (
                <TextPanel post={post} theme={theme} kit={kit} activeSlot={activeSlot} fromReview={fromReview} onContent={setContent} onContents={setContents} onTune={tune} onPickReview={pickReview} onTypeOwnReview={typeOwnReview} />
              )}
              {tool === 'photos' && (
                <PhotosPanel post={post} activeSlot={activeSlot} onPickSlot={setPicking} onMovePhoto={movePhoto} />
              )}
              {tool === 'caption' && (
                <CaptionPanel post={post} caption={caption} salonName={salonName} town={town} onUpdate={update} />
              )}
            </div>
          </aside>
        )}

        <div className="sx-stage" onClick={(e) => { if (phone && tool && (e.target === e.currentTarget || e.target.classList.contains('sx-stage-canvas'))) { hapticTick(); setTool(null); setTall(false) } }}>
          <div className="sx-stage-top">
            <Seg label="Format" value={format} onChange={(v) => { update({ format: v }, 'format'); setSlideIndex(0) }} options={[{ id: 'post', label: phone ? 'Post' : 'Post 4:5' }, { id: 'story', label: phone ? 'Story' : 'Story 9:16' }]} />
            <button type="button" className={`sx-btn sx-ig-toggle${ig ? ' is-on' : ''}`} aria-pressed={ig} onClick={() => { hapticTick(); setIg(v => !v) }}>
              <InstagramIcon size={16} /><span>{phone ? 'Preview' : 'See it on Instagram'}</span>
            </button>
            <button type="button" className={`sx-btn sx-fav-toggle${faved ? ' is-on' : ''}`} aria-pressed={!!faved} aria-label={faved ? 'Favourite. Tap to take it out' : 'Save as a favourite'} title={faved ? 'In your favourites' : 'Save as a favourite'} onClick={(e) => toggleFavourite(e.currentTarget)}>
              <HeartIcon size={16} filled={!!faved} />{!phone && <span>Favourite</span>}
            </button>
          </div>
          <div className={`sx-stage-canvas${magic ? ' is-magic' : ''}`} ref={stageRef}>
            {stage.w > 0 && (ig
              ? <InstagramFrame format={format} width={canvasW} name={kit.name} logo={kit.logo} caption={caption}>{canvas}</InstagramFrame>
              : canvas)}
          </div>
          {slides.length > 1 && (
            <div className="sx-slides">
              <button type="button" className="sx-icon-btn" disabled={slide === 0} onClick={() => setSlideIndex(slide - 1)} aria-label="Previous picture"><ChevronLeft /></button>
              <div className="sx-slide-strip">
                {slides.map((s, i) => (
                  <button key={i} type="button" className={`sx-slide-thumb${i === slide ? ' is-on' : ''}`} onClick={() => { hapticTick(); setSlideIndex(i) }} aria-label={`Picture ${i + 1}`} aria-current={i === slide}>
                    <PostCanvas post={post} theme={theme} format={format} slideIndex={i} width={44} thumb />
                  </button>
                ))}
              </div>
              <button type="button" className="sx-icon-btn" disabled={slide === slides.length - 1} onClick={() => setSlideIndex(slide + 1)} aria-label="Next picture"><ChevronRight /></button>
            </div>
          )}
          {!phone && <p className="sx-stage-hint">Tap words to change them. Drag a photo to move it.</p>}
        </div>
      </div>

      {toast && (
        <div className="sx-toast" role="status" key={toast.at}>
          <span>{toast.text}</span>
          {toast.undo && history.canUndo && <button type="button" onClick={() => { hapticTick(); history.undo(); setToast(null) }}><UndoIcon size={14} />Undo</button>}
          {toast.action && <button type="button" onClick={() => { hapticTick(); toast.action.run(); setToast(null) }}><UndoIcon size={14} />{toast.action.label}</button>}
        </div>
      )}
      {picking && (
        <PhotoPicker
          trade={trade || post.trade}
          salon={salon}
          slot={picking}
          allowStock={post.type !== 'beforeAfter'}
          onClose={() => setPicking(null)}
          onPick={(src) => { movePhoto(picking, { src, fx: 0.5, fy: 0.4, zoom: 1 }, `pick-${picking}`); setPicking(null); hapticTick() }}
        />
      )}
      {sharing && (
        <ShareSheet
          post={{ ...withIds(post), caption: post.caption || caption }}
          theme={theme}
          onClose={() => setSharing(false)}
          onChange={(patch) => update(patch, 'share-change')}
          onEdit={(t) => { setSharing(false); setTool(t === 'words' ? 'text' : t) }}
          onPosted={(saved) => { history.set(p => ({ ...p, status: saved.status, postedAt: saved.postedAt }), { record: false }) }}
          onBeforeShare={() => {
            dirty.current = true
            const saved = savePost({ ...withIds(post), caption: post.caption || caption })
            ids.current = { id: saved.id, createdAt: saved.createdAt }
            return saved
          }}
        />
      )}
    </CreatorFrame>
  )
}

export default PostStudio
